import { useRef } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

/**
 * TopicHeroIntro - sequenced hero entrance for the 19 routes built by the
 * topicPage factory in public/pentabay-subpages.js.
 *
 * Companion to HeroIntro.jsx, which owns the home hero and nothing else. Same
 * shape of component for the same reasons: the markup is an injected HTML
 * string, so there is no JSX to scope a ref to, the element is located by
 * querying the DOM, and useGSAP still earns its place because its context owns
 * every tween built inside the callback and reverts them on cleanup.
 *
 * ── What the hero actually is ─────────────────────────────────────────────
 * Measured on all 19 routes rather than read off the template, because two
 * later scripts rewrite this markup at runtime:
 *
 *   section.hero-shell                <- data-reveal is NOT on it (see below)
 *     div.aura                        display:none on banner routes
 *     svg[aria-hidden]                display:none on banner routes
 *     div.shell.hero-grid             the only in-flow child
 *       div.flex…      eyebrow + optional .topic-mark
 *       h1.t-h1
 *       p.t-lead
 *       div.mt-8…      the CTA row
 *
 * So the elements to sequence are the children of the COPY WRAPPER, not of the
 * <section>. Sequencing the section's own children would animate two
 * display:none decorations and then the entire copy block as one unit, which is
 * not an entrance at all. 17 of the 19 routes have exactly the four copy
 * children above; /about and /careers are restructured by
 * pentabay-page-imagery.js into div.hero-copy + a figure, i.e. two children.
 * Nothing here hardcodes four, so both shapes sequence correctly.
 *
 * ── The banner is a background, not an image element ──────────────────────
 * The brief asks for the full-bleed banner image to fade opacity 0 -> 1 with no
 * y-translate. There is no image element to tween: pentabay-page-imagery.js
 * adds .pb-hero-banner and sets --pb-hero-banner, and the art is painted as the
 * middle layer of a three-layer background-image stack on the <section> itself
 * (scrim / art / fallback gradient - see "SERVICE + INDUSTRY HERO BANNERS" in
 * src/StyleOverrides.jsx). Measured: zero <img> elements in the hero on all 17
 * banner routes.
 *
 * Tweening the section's own opacity is not the answer - the copy is a
 * DESCENDANT, so it would be multiplied by the section's fade and the copy's
 * specified 0.7s power2.out curve would not be what renders. Instead a curtain
 * is laid over the background only: an opaque layer painted the same brand blue
 * the banner falls back to when the art 404s, faded 1 -> 0. The art therefore
 * arrives 0 -> 1, opacity only, with the copy sequence untouched on top of it.
 * .shell already carries z-index:1 on banner routes and the section already
 * carries isolation:isolate, so the curtain slots underneath the copy and stays
 * confined to the hero. It is removed outright when the tween finishes.
 *
 * The two colour literals in CURTAIN_CSS MIRROR StyleOverrides' banner
 * fallback (background-color #0F4FBA + the 135deg gradient). They are literals
 * there too - this blue field is not tokenised - but they are a duplication, so
 * they have to move together. If they ever drift the symptom is a one-frame
 * colour pop at the start of the hero entrance.
 *
 * ── runHeroChars is NOT neutralised here, and does not need to be ─────────
 * HeroIntro has to neutralise it because the home <h1> carries
 * [data-hero-h1], which is the only thing runHeroChars() (Pentabay.html ~5739)
 * looks for - it queries `scope.querySelector('[data-hero-h1]')` and returns
 * immediately when that is absent. topicPage renders a plain
 * `h1.t-h1`. Measured across all 19 routes after routeNow() had run
 * runHeroChars(target): 0 occurrences of [data-hero-h1] and 0 .word spans, i.e.
 * the h1 is never split and there is no WAAPI word cascade to fight.
 *
 * A copy of HeroIntro's .word neutraliser scoped at these heroes would be dead
 * CSS, and a second style block asserting something no code does is the kind of
 * thing that later reads as load-bearing. If [data-hero-h1] is ever added to
 * the topicPage hero this becomes a real requirement: widen the existing rule
 * in HeroIntro.jsx (HERO_INTRO_CSS) to cover these heroes rather than adding a
 * second mechanism here.
 *
 * ── Route lifecycle ──────────────────────────────────────────────────────
 * HeroIntro can disconnect its observer after the first hit and time out after
 * 10s, because the home hero exists once per session. These 19 routes are
 * reached by client-side navigation, and PB_parkPage() DETACHES the previous
 * page (node.remove(), stashed for reuse) while PB_ensurePage() re-attaches the
 * same node later. A one-shot observer would therefore animate only the first
 * topic route visited and silently skip the other 18, so the observer is kept
 * for the session and idempotency is per ELEMENT (data-pb-topic-hero) rather
 * than per component instance - the same correction CtaReveal.jsx documents.
 * The observer callback is still the HeroIntro pattern and for the HeroIntro
 * reason: MutationObserver callbacks are microtasks delivered before paint, so
 * the opening gsap.set() lands on the frame the markup does. No setTimeout, no
 * polling - both flash unstyled content.
 *
 * The route guard is not optional. The inline router stamps
 * <html data-route> while the OUTGOING page is still main.page.active, so
 * building on whatever is active at that moment annotates a node that is about
 * to be detached and the real hero is never touched - a silent failure this
 * codebase has already had. page.id must equal `page-${route}`.
 */

/* The topicPage routes. An explicit allowlist rather than a shape probe because
   public/pentabay-pages.js renders /services, /industries, /compliance, /trust
   and /insights from its own hero() helper, whose hero is nearly the same shape
   (the only structural difference is that its eyebrow is a direct child of
   .shell instead of sitting in a flex row). Guessing between the two on shape
   would be fragile; the factory's route list is a fact. Keep in sync with the
   topicPage({…}) call sites. */
const ROUTES = new Set([
  'custom-software', 'ai-data', 'cloud-devops', 'design', 'healthcare',
  'fintech', 'public-sector', 'mobile-development', 'saas-platforms',
  'digital-marketing', 'low-code-platforms', 'banking-lending',
  'telecommunications', 'ai-edtech', 'saas-b2b', 'real-estate',
  'enterprise-it', 'about', 'careers',
  // 2026-09-21: Digital Marketing + Cybersecurity families. All six are
  // topicPage({...}) call sites, so they take the same hero treatment.
  'seo-services', 'ppc-google-ads',
  'cybersecurity', 'cybersecurity-assessment', 'managed-cybersecurity',
  'penetration-testing',
])

/* 2026-08 intensity pass, matched to HeroIntro so the two heroes read the same:
   20px -> 56px, 0.7s -> 0.55s, power2.out -> back.out(1.6) for a settle. */
const DURATION = 0.55
const STAGGER = 0.15   // between successive copy steps
const SHIFT = 56
const EASE = 'back.out(1.6)'

/* The curtain fades the banner art in and is OPACITY ONLY, so it cannot use
   EASE: back.out's control point overshoots past the end value, and an opacity
   driven past 1 is clamped by the browser - the tween would visibly sit at full
   for the tail of its duration instead of easing into it. expo.out gives the
   same fast-then-decelerate punch with no overshoot to clamp. */
const CURTAIN_EASE = 'expo.out'

const BUILT = 'pbTopicHero'          // dataset key -> data-pb-topic-hero
const CURTAIN = 'pb-topic-hero-curtain'

/* Injected at module scope, not from an effect: an effect would land a frame
   after HtmlPage writes the page in, and the curtain would paint at its base
   opacity for that frame. Nothing here hides copy - constraint 7, fail visible:
   if this component never runs, the hero is simply the finished hero. */
const CURTAIN_CSS = `
.${CURTAIN} {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: #0F4FBA;
  background-image: linear-gradient(135deg, #1E5FD4 0%, #0F4FBA 55%, #0A2E6E 100%);
}
`
if (typeof document !== 'undefined' && !document.getElementById('pb-topic-hero-css')) {
  const el = document.createElement('style')
  el.id = 'pb-topic-hero-css'
  el.textContent = CURTAIN_CSS
  document.head.appendChild(el)
}

/* Located by structure and class only. Never by text: TranslationManager
   rewrites these text nodes at runtime for fr / es / zh-CN, so any copy-based
   lookup would resolve on English and silently miss everywhere else. */
function findHero() {
  const route = document.documentElement.getAttribute('data-route')
  if (!route || !ROUTES.has(route)) return null
  const page = document.querySelector('main.page.active')
  // The guard. See the route-lifecycle note above.
  if (!page || page.id !== `page-${route}`) return null
  const hero = page.querySelector(':scope > section.hero-shell')
  if (!hero || hero.dataset[BUILT] === '1') return null
  // The copy wrapper is the hero's only in-flow child on banner routes.
  const copy = hero.querySelector(':scope > .shell')
  if (!copy || !copy.children.length) return null
  return { hero, copy }
}

export default function TopicHeroIntro() {
  // One matchMedia context per built hero, all reverted on unmount.
  const mms = useRef([])

  useGSAP(() => {
    let observer = null

    const build = () => {
      const found = findHero()
      if (!found) return
      const { hero, copy } = found

      const steps = Array.from(copy.children)
      if (!steps.length) return

      hero.dataset[BUILT] = '1'

      /* Opt out of RevealFixer's WAAPI cascade BEFORE it can fire. Both are
         needed: rvPlayed makes playStagger() bail, and .in stops the
         [data-reveal] CSS holding the block at opacity 0. Measured, this hero
         carries no [data-reveal] today, so both are currently inert - they are
         set anyway because WAAPI composites above inline styles, and if a
         data-reveal is ever added to this section the two systems would fight
         for 780ms with no other symptom than a visible snap. */
      hero.dataset.rvPlayed = '1'
      hero.classList.add('in')

      const mm = gsap.matchMedia()
      mms.current.push(mm)

      /* Both branches exist so flipping the OS setting reverts cleanly - GSAP
         reverts the no-preference context when its query stops matching.
         House rule: under reduce, motion does not EXIST. So this branch sets
         nothing at all: no gsap.set, no pre-hide, no curtain, no shortened
         version. The hero paints exactly as the stylesheet leaves it. */
      mm.add('(prefers-reduced-motion: reduce)', () => {})

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // Lands in the same frame the markup does, so nothing flashes in at
        // full opacity and then snaps to hidden.
        gsap.set(steps, { opacity: 0, y: SHIFT, willChange: 'transform, opacity' })

        /* The banner: opacity only, no y. A full-bleed layer rising 20px would
           uncover the edge it is supposed to be bleeding off. It gets its own
           tween rather than a slot in the copy timeline because of the ordering
           problem below - the two still start within a frame of each other, and
           they are separate treatments in the brief, not successive steps.

           ORDERING. .pb-hero-banner is not in the markup; pentabay-page-imagery
           .js adds it on pb-pages-mounted. React mounts this component long
           before HtmlPage injects and runs Pentabay.html's scripts, so OUR
           listener for that event is registered first and therefore runs first:
           at build time the class is reliably NOT there yet. Measured - an
           earlier version read the class once here and the curtain was never
           created on any of the 17 banner routes, while every other check
           passed. So the class is watched for instead of raced. */
        let curtain = null
        let curtainTween = null
        let classObs = null

        const addCurtain = () => {
          if (curtain || !hero.classList.contains('pb-hero-banner')) return false
          curtain = document.createElement('div')
          curtain.className = CURTAIN
          curtain.setAttribute('aria-hidden', 'true')
          // First child: under .shell (z-index:1), over the section's own
          // background layers.
          hero.insertBefore(curtain, hero.firstChild)
          curtainTween = gsap.fromTo(curtain,
            { opacity: 1 },
            {
              opacity: 0,
              duration: DURATION,
              ease: CURTAIN_EASE,
              onComplete() {
                curtain.remove()
                curtain = null
              },
            },
          )
          return true
        }

        if (!addCurtain()) {
          classObs = new MutationObserver(() => {
            if (addCurtain()) { classObs.disconnect(); classObs = null }
          })
          classObs.observe(hero, { attributes: true, attributeFilter: ['class'] })
        }

        const tl = gsap.timeline({
          defaults: { duration: DURATION, ease: EASE },
          onComplete() {
            // Hand the layers back; leaving will-change on the copy for the
            // whole session is worse than not setting it.
            gsap.set(steps, { clearProps: 'opacity,transform,willChange' })
            /* By now imagery has long since run. On /about and /careers it
               never tags this hero at all, so without this the observer would
               sit on the node for the rest of the session waiting for a class
               that is not coming. */
            if (classObs) { classObs.disconnect(); classObs = null }
          },
        })

        // Copy, in DOM order: each step starts STAGGER after the previous one
        // BEGAN, matching HeroIntro's cadence.
        steps.forEach((el, i) => {
          tl.to(el, { opacity: 1, y: 0 }, i === 0 ? 0 : `<${STAGGER}`)
        })

        return () => {
          tl.kill()
          if (curtainTween) curtainTween.kill()
          if (classObs) { classObs.disconnect(); classObs = null }
          if (curtain) { curtain.remove(); curtain = null }
          gsap.set(steps, { clearProps: 'opacity,transform,willChange' })
        }
      })
    }

    build()

    /* Kept for the session: every one of the 19 routes mounts its own page node
       on first view, and a parked route re-attaches the SAME node later. The
       element flag makes repeat calls cheap, and the route check is the first
       thing findHero() does, so the common case is one Set lookup. */
    observer = new MutationObserver(build)
    observer.observe(document.body, { childList: true, subtree: true })
    // data-route flips on client-side navigation without a childList mutation
    // when the page node is re-attached from the park stash.
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    window.addEventListener('pb-pages-mounted', build)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('pb-pages-mounted', build)
      for (const m of mms.current) m.revert()
      mms.current = []
    }
  }, { dependencies: [] })   // [] => builds on mount only, never on re-render

  return null
}
